import { Receipt, Banknote, CreditCard, Building2, HandCoins, Undo2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { TRY, NUM } from "@/lib/format";
import { r2 } from "@/lib/pos";

// Filtrelenmiş fişlerden özet: adet · ciro · ödeme kırılımı (karma fişler parçalarına dağıtılır) · iptal/iade
export const summarize = (sales) => {
  const by = { nakit: 0, kredi_karti: 0, havale: 0, veresiye: 0 };
  let count = 0, total = 0, cancelled = 0, returned = 0;
  for (const s of sales) {
    if (s.cancelled) { cancelled++; continue; }
    count++;
    const g = Number(s.grand_total) || 0;
    total += g;
    returned += Number(s.returned_total) || 0;
    if (s.payment_method === "karma" && Array.isArray(s.payments) && s.payments.length) {
      for (const p of s.payments) if (by[p.method] != null) by[p.method] += Number(p.amount) || 0;
    } else {
      const m = by[s.payment_method] != null ? s.payment_method : "nakit";
      by[m] += g;
    }
  }
  return { count, total: r2(total), avg: count ? r2(total / count) : 0, cancelled, returned: r2(returned), by: Object.fromEntries(Object.entries(by).map(([k, v]) => [k, r2(v)])) };
};

const PAYS = [
  ["nakit", "Nakit", Banknote, "text-emerald-600 dark:text-emerald-400", "bg-emerald-500"],
  ["kredi_karti", "Kredi Kartı", CreditCard, "text-blue-600 dark:text-blue-400", "bg-blue-500"],
  ["havale", "Havale / EFT", Building2, "text-violet-600 dark:text-violet-400", "bg-violet-500"],
  ["veresiye", "Veresiye", HandCoins, "text-amber-600 dark:text-amber-400", "bg-amber-500"],
];

const Kpi = ({ label, value, sub, icon: Icon, accent, testid }) => (
  <div className="rounded-xl border border-border bg-card px-3.5 py-3 min-w-0" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</div>
    <div className={cn("font-head font-black tnum leading-none mt-1.5 text-xl truncate", accent ? "text-primary" : "text-foreground")}>{value}</div>
    {sub && <div className="text-[10.5px] text-muted-foreground mt-1 truncate">{sub}</div>}
  </div>
);

// Özet şeridi: fiş adedi · ciro · ortalama · iptal/iade + ödeme kırılım çubuğu
export function HistorySummary({ sales, label }) {
  const s = summarize(sales);
  const parts = PAYS.map(([k, l, I, tc, bc]) => ({ k, l, I, tc, bc, v: s.by[k] })).filter((p) => p.v > 0);
  return (
    <div className="space-y-2.5" data-testid="qs-history-summary">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <Kpi label={`Fiş · ${label}`} value={NUM(s.count)} sub={s.cancelled ? `${s.cancelled} iptal` : "iptal yok"} icon={Receipt} testid="qs-sum-count" />
        <Kpi label="Ciro (KDV dahil)" value={TRY(s.total)} sub={s.returned > 0 ? `İade ${TRY(s.returned)}` : "iade yok"} icon={Banknote} accent testid="qs-sum-total" />
        <Kpi label="Ortalama Fiş" value={TRY(s.avg)} sub="fiş başına" testid="qs-sum-avg" />
        <Kpi label="Veresiye (açık yazılan)" value={TRY(s.by.veresiye)} sub={s.by.veresiye > 0 ? "cari defterde" : "—"} icon={HandCoins} testid="qs-sum-veresiye" />
      </div>
      {s.total > 0 && (
        <div className="rounded-xl border border-border bg-card px-3.5 py-2.5" data-testid="qs-sum-paybar">
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted">
            {parts.map((p) => <span key={p.k} className={cn("h-full transition-all", p.bc)} style={{ width: `${Math.max(1.5, (p.v / s.total) * 100)}%` }} title={`${p.l} ${TRY(p.v)}`} />)}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {PAYS.map(([k, l, I, tc, bc]) => (
              <span key={k} className="inline-flex items-center gap-1.5 text-[11px]" data-testid={`qs-sum-pay-${k}`}>
                <span className={cn("h-2 w-2 rounded-full", bc)} /><I className={cn("h-3.5 w-3.5", tc)} /><span className="text-muted-foreground">{l}</span>
                <b className="tnum text-foreground">{TRY(s.by[k])}</b>
                {s.total > 0 && <span className="text-muted-foreground tnum">%{NUM(r2((s.by[k] / s.total) * 100))}</span>}
              </span>
            ))}
            {s.cancelled > 0 && <span className="inline-flex items-center gap-1 text-[11px] text-destructive ml-auto"><Undo2 className="h-3.5 w-3.5" />{s.cancelled} iptal (ciroya dahil değil)</span>}
          </div>
        </div>
      )}
    </div>
  );
}
